import React, { useState, useEffect, useMemo } from 'react';
import { Empresa } from '@/types/empresa';
import { useAuth } from '@/hooks/useAuth';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Switch } from '@/components/ui/Switch';
import { empresaService } from '@/lib/api/empresaService';
import { pagadorasService } from '@/lib/api/pagadorasService';
import { User } from '@/types/user';
import { getAsesoresCached } from '@/hooks/useSharedData';

interface EmpresaFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Partial<Empresa>) => Promise<void>;
  initialData?: Partial<Empresa>;
}

export const EmpresaFormModal: React.FC<EmpresaFormModalProps> = ({ isOpen, onClose, onSubmit, initialData }) => {
  const { isFieldAllowed } = useAuth();
  const canNombre = isFieldAllowed('nombre');
  const canAgrupacion = isFieldAllowed('agrupacion');
  const canEmpresaPagadora = isFieldAllowed('empresaPagadora');
  const canEmpresaSegmentacionCobranza = isFieldAllowed('empresaSegmentacionCobranza');
  const canTipo = isFieldAllowed('tipo');
  const canRegion = isFieldAllowed('region');
  const canAutorizacionDG = isFieldAllowed('autorizacionDG');
  const canVoboRequerido = isFieldAllowed('voboRequerido');
  const canEstatus = isFieldAllowed('estatus');
  const canNivel = isFieldAllowed('nivel');

  const [formData, setFormData] = useState<Partial<Empresa>>({});
  const [loading, setLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [asesores, setAsesores] = useState<User[]>([]);
  const [asesoresSeleccionados, setAsesoresSeleccionados] = useState<string[]>([]);
  const [loadingAsignaciones, setLoadingAsignaciones] = useState(false);
  const [pagadorasMap, setPagadorasMap] = useState<Record<string, string[]>>({});
  const pagadorasList = useMemo(() => Object.keys(pagadorasMap).sort((a, b) => a.localeCompare(b)), [pagadorasMap]);
  const segmentosList = useMemo(() => pagadorasMap[formData.empresaPagadora || ''] || [], [pagadorasMap, formData.empresaPagadora]);

  useEffect(() => {
    if (!isOpen) { setIsDirty(false); return; }
    if (isOpen) {
      setIsDirty(false);
      pagadorasService.get().then((resp) => {
        if (resp.data && typeof resp.data === 'object') {
          setPagadorasMap(resp.data);
        }
      }).catch(() => setPagadorasMap({}));

      setFormData(initialData || {
        nombre: '',
        agrupacion: '',
        empresaPagadora: '',
        empresaSegmentacionCobranza: '',
        tipo: 'Interna',
        region: 'CDMX',
        autorizacionDG: 'Normal',
        estatus: 'activa',
        voboRequerido: 'No',
        nivel: '',
      });
      
      // Cargar asesores (desde caché compartido)
      getAsesoresCached().then(setAsesores);

      // Si es edición, cargar asignaciones existentes
      if (initialData?.id) {
        setLoadingAsignaciones(true);
        empresaService.getAsignaciones(initialData.id).then(resp => {
          if (resp.data) {
            setAsesoresSeleccionados(resp.data);
          }
        }).finally(() => {
          setLoadingAsignaciones(false);
        });
      } else {
        setAsesoresSeleccionados([]);
      }
    }
  }, [isOpen, initialData]);

  // Filtrar asesores por la región seleccionada
  const asesoresFiltrados = useMemo(() => {
    if (!formData.region) return asesores;
    return asesores.filter(u => (u.region || '') === formData.region);
  }, [asesores, formData.region]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (canNombre && !formData.nombre) {
      alert('El nombre es requerido');
      return;
    }
    // Misma lógica que empresas existentes: agrupación por defecto = nombre si está vacía
    const agrupacion = (formData.agrupacion || '').trim() || (formData.nombre || '').trim();
    const dataToSubmit = {
      ...formData,
      agrupacion: agrupacion || undefined,
      asesoresAsignados: Array.isArray(asesoresSeleccionados) ? asesoresSeleccionados : [],
    };
    if (!dataToSubmit.id) {
      dataToSubmit.id = Date.now().toString();
    }
    setLoading(true);
    await onSubmit(dataToSubmit);
    setIsDirty(false);
    setLoading(false);
  };

  const handleChange = (field: keyof Empresa, value: any) => {
    setIsDirty(true);
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleAsesor = (ownerID: string) => {
    setIsDirty(true);
    setAsesoresSeleccionados(prev => {
      if (prev.includes(ownerID)) {
        return prev.filter(id => id !== ownerID);
      } else {
        return [...prev, ownerID];
      }
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}  title={initialData?.id ? "Editar Empresa" : "Nueva Empresa"}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Formulario estilo legacy */}
        <div className="max-h-[60vh] overflow-y-auto">
          {/* Campos del formulario */}
          <div className="space-y-3 px-2">
            {canNombre && (
            <div className="flex items-center gap-4">
              <label className="w-40 text-right font-medium text-gray-600 text-sm shrink-0">
                Nombre <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                value={formData.nombre || ''}
                onChange={(e) => handleChange('nombre', e.target.value)}
                required
              />
            </div>
            )}

            {canAgrupacion && (
            <div className="flex items-center gap-4">
              <label className="w-40 text-right font-medium text-gray-600 text-sm shrink-0">
                Agrupación <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                value={formData.agrupacion || ''}
                onChange={(e) => handleChange('agrupacion', e.target.value)}
                required
              />
            </div>
            )}

            {initialData?.id && canEmpresaPagadora && (
              <div className="flex items-center gap-4">
                <label className="w-40 text-right font-medium text-gray-600 text-sm shrink-0">
                  Pagadora
                </label>
                <select
                  className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                  value={formData.empresaPagadora || ''}
                  onChange={(e) => {
                    handleChange('empresaPagadora', e.target.value);
                    // Limpiar segmento si la nueva pagadora no tiene el valor actual
                    const nuevosSegmentos = pagadorasMap[e.target.value] || [];
                    if (!nuevosSegmentos.includes(formData.empresaSegmentacionCobranza || '')) {
                      handleChange('empresaSegmentacionCobranza', '');
                    }
                  }}
                >
                  <option value="">Seleccionar pagadora...</option>
                  {pagadorasList.map((nombre) => (
                    <option key={nombre} value={nombre}>{nombre}</option>
                  ))}
                </select>
              </div>
            )}

            {initialData?.id && canEmpresaSegmentacionCobranza && (
              <div className="flex items-center gap-4">
                <label className="w-40 text-right font-medium text-gray-600 text-sm shrink-0">
                  Segmentación de Cobranza
                </label>
                <select
                  className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                  value={formData.empresaSegmentacionCobranza || ''}
                  onChange={(e) => handleChange('empresaSegmentacionCobranza', e.target.value)}
                  disabled={!formData.empresaPagadora}
                >
                  <option value="">{formData.empresaPagadora ? 'Seleccionar segmento...' : 'Selecciona primero una pagadora'}</option>
                  {segmentosList.map((seg) => (
                    <option key={seg} value={seg}>{seg}</option>
                  ))}
                </select>
              </div>
            )}

            {canTipo && (
            <div className="flex items-center gap-4">
              <label className="w-40 text-right font-medium text-gray-600 text-sm shrink-0">
                Tipo <span className="text-red-500">*</span>
              </label>
              <select
                className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                value={formData.tipo || 'Interna'}
                onChange={(e) => handleChange('tipo', e.target.value)}
                required
              >
                <option value="Interna">Interna</option>
                <option value="Externa">Externa</option>
              </select>
            </div>
            )}

            {canRegion && (
            <div className="flex items-center gap-4">
              <label className="w-40 text-right font-medium text-gray-600 text-sm shrink-0">
                Región <span className="text-red-500">*</span>
              </label>
              <select
                className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                value={formData.region || 'CDMX'}
                onChange={(e) => handleChange('region', e.target.value)}
                required
              >
                <option value="CDMX">CDMX</option>
                <option value="Guadalajara">Guadalajara</option>
              </select>
            </div>
            )}

            {canAutorizacionDG && (
            <div className="flex items-center gap-4">
              <label className="w-40 text-right font-medium text-gray-600 text-sm shrink-0">
                Autorización DG <span className="text-red-500">*</span>
              </label>
              <select
                className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                value={formData.autorizacionDG || 'Normal'}
                onChange={(e) => handleChange('autorizacionDG', e.target.value)}
                required
              >
                <option value="Normal">Normal</option>
                <option value="Estricta">Estricta</option>
              </select>
            </div>
            )}

            {canVoboRequerido && (
            <div className="flex items-center gap-4">
              <label className="w-40 text-right font-medium text-gray-600 text-sm shrink-0">
                Se Requiere VoBo <span className="text-red-500">*</span>
              </label>
              <select
                className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                value={formData.voboRequerido || ''}
                onChange={(e) => handleChange('voboRequerido', e.target.value)}
                required
              >
                <option value="">Seleccionar...</option>
                <option value="Sí">Sí</option>
                <option value="No">No</option>
                <option value="Sólo Administrativos">Sólo Administrativos</option>
              </select>
            </div>
            )}

            {canEstatus && (
            <div className="flex items-center gap-4">
              <label className="w-40 text-right font-medium text-gray-600 text-sm shrink-0">
                Estatus <span className="text-red-500">*</span>
              </label>
              <select
                className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                value={formData.estatus || 'activa'}
                onChange={(e) => handleChange('estatus', e.target.value)}
                required
              >
                <option value="activa">Activa</option>
                <option value="congelada">Congelada</option>
                <option value="baja">Baja</option>
              </select>
            </div>
            )}

            {canNivel && (
            <div className="flex items-center gap-4">
              <label className="w-40 text-right font-medium text-gray-600 text-sm shrink-0">
                Nivel <span className="text-red-500">*</span>
              </label>
              <select
                className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                value={formData.nivel || ''}
                onChange={(e) => handleChange('nivel', e.target.value)}
                required
              >
                <option value="">Seleccionar...</option>
                <option value="Riesgo 1">Riesgo 1</option>
                <option value="Riesgo 2">Riesgo 2</option>
                <option value="Riesgo 3">Riesgo 3</option>
                <option value="Riesgo 4">Riesgo 4</option>
                <option value="Riesgo 5">Riesgo 5</option>
              </select>
            </div>
            )}
          </div>

          {/* Sección de Asesores */}
          <div className="mt-4 border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-4 py-2 bg-green-100">
              <div className="font-medium text-gray-700 text-center">Asesores</div>
              <div className="text-xs text-gray-500 text-center">
                Selecciona los asesores que tendrán acceso a esta empresa
                {formData.region && <span className="font-medium"> (Región: {formData.region})</span>}
              </div>
            </div>

            {/* Lista de asesores con switches */}
            <div className="divide-y divide-gray-200">
              {loadingAsignaciones ? (
                <div className="px-4 py-4 text-center text-gray-500 text-sm">
                  Cargando asignaciones...
                </div>
              ) : asesoresFiltrados.length === 0 ? (
                <div className="px-4 py-4 text-center text-gray-500 text-sm">
                  No hay asesores activos en esta región.
                </div>
              ) : (
                asesoresFiltrados.map(asesor => (
                  <div key={asesor.ownerID} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50">
                    <div>
                      <div className="font-medium text-gray-900 text-sm">{asesor.name}</div>
                      <div className="text-xs text-gray-500">{asesor.region || '—'}</div>
                    </div>
                    <Switch
                      checked={asesoresSeleccionados.includes(asesor.ownerID)}
                      onChange={() => toggleAsesor(asesor.ownerID)}
                      color="blue"
                    />
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? 'Guardando...' : 'Guardar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
